import { useTranslation } from 'react-i18next';
import { Banner, Button, Input, Label } from '@knack/asterisk-react';

import { type Credentials } from '@/types/auth';
import { useApplicationQuery } from '@/hooks/api/queries/useApplicationQuery';
import { useSignIn2faForm } from '@/hooks/useSignIn2faForm';
import { useThemingContext } from '@/context/ThemingContext';

export function SignIn2fa({ credentials }: { credentials: Credentials }) {
  const [t] = useTranslation();
  const { data: application } = useApplicationQuery();
  const { theme } = useThemingContext();
  const logoWidth = 150 * (theme.mainNavContainer.logo.imageWidthPercentage / 100);
  const { register, handleSubmit, errors, isSubmitting, isValid, onSubmitHandler } =
    useSignIn2faForm({ credentials });

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)} className="w-full">
      {theme.mainNavContainer.logo.isVisible && (
        <div className="mb-4 flex w-full items-center justify-center gap-2 sm:mb-6 sm:justify-center">
          <img
            src={theme.mainNavContainer.logo.url}
            alt={application?.name}
            style={{ minWidth: `${logoWidth}px`, maxWidth: `${logoWidth}px` }}
          />
        </div>
      )}
      <h3
        className="mb-4 self-start text-center text-2xl text-emphasis sm:mb-6 sm:self-center"
        data-testid="twofa-title"
      >
        {t('components.two_factor_auth.enter_code_title')}
      </h3>
      {errors.code2fa && (
        <Banner
          intent="destructive"
          className="mb-8 flex w-full justify-center"
          data-testid="twofa-error-banner"
        >
          <Banner.Message className="text-center">
            {t('components.two_factor_auth.enter_code_error')}
          </Banner.Message>
        </Banner>
      )}
      <Label htmlFor="email" intent={errors.code2fa && 'destructive'}>
        {t('components.two_factor_auth.6_digit_code')}
      </Label>
      <Input
        title={t('components.two_factor_auth.6_digit_code')}
        id="2fa-number"
        disabled={isSubmitting}
        maxLength={6}
        placeholder={t('components.two_factor_auth.6_digit_code_placeholder')}
        intent={errors.code2fa && 'destructive'}
        autoFocus
        className="mt-2"
        data-testid="twofa-input-code"
        {...register('code2fa', {
          onChange: (e) => {
            const value = e.target.value.replace(/[^0-9]/g, '');
            e.target.value = value;
          }
        })}
      />
      <Button
        type="submit"
        intent="primary"
        isLoading={isSubmitting}
        disabled={!isValid}
        className="mb-8 mt-8 w-full"
        data-testid="twofa-submit-button"
      >
        {t('components.two_factor_auth.submit')}
      </Button>
      <p className="font-emphasis text-center">{t('components.two_factor_auth.contact_admin')}</p>
    </form>
  );
}
